import { useEffect, useState, useCallback } from "react";
import { Slide } from "@mui/material";
import Styles from "./styles";
import { VeryBtnTransparent } from "../../common/v2/VeryBtnTransparent";
import { classVeryBtnTransparent } from "../../common/v2/VeryBtnTransparent/classVeryBtnTransparent";
import { classVeryIconGoogle } from "../../common/v2/VeryIconGoogle/classVeryIconGoogle";
import { getTripLocationLogs } from "../../../services/trip";
import { useSessionContext } from "../../../context/session.context";
import { useTripsContext } from "../../../context";

// Helper to dynamically load Leaflet from CDN
const loadLeaflet = () => {
  return new Promise((resolve) => {
    if (window.L) {
      resolve(window.L);
      return;
    }
    
    // Check if stylesheet is already in document
    let link = document.querySelector('link[href*="leaflet"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }

    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.onload = () => resolve(window.L);
    document.body.appendChild(script);
  });
};

export const TripRouteLogPanel = ({ tripId = 0, onClose, passengerName = "", driverName = "", rowId = -1 }) => {
  const session = useSessionContext();
  const trips = useTripsContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null); // { logs: [] }

  const addresses = trips.getTripAddresses(rowId);

  const isOpen = tripId > 0;

  const fetchRoute = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    setError("");
    setData(null);
    const resp = await getTripLocationLogs(tripId, session.value.jwt);
    setLoading(false);
    if (resp.error) {
      setError(resp.error.message || "No fue posible cargar la ruta registrada");
      return;
    }
    setData(resp.body);
  }, [tripId, session.value.jwt]);

  useEffect(() => {
    fetchRoute();
  }, [fetchRoute]);

  // Leaflet map lifecycle
  useEffect(() => {
    let map = null;
    if (isOpen && data && data.logs && data.logs.length > 0) {
      loadLeaflet().then((L) => {
        const container = document.getElementById("leaflet-route-map");
        if (!container) return;

        // Initialize map (Mazatlan fallback coords)
        map = L.map("leaflet-route-map").setView([23.2494, -106.4111], 13);
        
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "© OpenStreetMap contributors",
        }).addTo(map);

        const passengerPoints = data.logs
          .filter((l) => l.userType === "PASSENGER")
          .map((l) => [l.lat, l.lon]);
        
        const driverPoints = data.logs
          .filter((l) => l.userType === "DRIVER")
          .map((l) => [l.lat, l.lon]);

        // Draw Passenger Route (Blue)
        if (passengerPoints.length > 0) {
          L.polyline(passengerPoints, { color: "#3b82f6", weight: 4, opacity: 0.85 }).addTo(map);
          
          L.marker(passengerPoints[0], {
            icon: L.divIcon({
              className: "leaflet-custom-dot",
              html: `<span style="background-color: #3b82f6; width: 12px; height: 12px; display: block; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></span>`,
              iconSize: [12, 12],
              iconAnchor: [6, 6]
            }),
          }).addTo(map).bindPopup("Inicio Pasajero");

          L.marker(passengerPoints[passengerPoints.length - 1], {
            icon: L.divIcon({
              className: "leaflet-custom-dot",
              html: `<span style="background-color: #1e3a8a; width: 12px; height: 12px; display: block; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></span>`,
              iconSize: [12, 12],
              iconAnchor: [6, 6]
            }),
          }).addTo(map).bindPopup("Último Punto Pasajero");
        }

        // Draw Driver Route (Orange)
        if (driverPoints.length > 0) {
          L.polyline(driverPoints, { color: "#f97316", weight: 4, opacity: 0.85 }).addTo(map);

          L.marker(driverPoints[0], {
            icon: L.divIcon({
              className: "leaflet-custom-dot",
              html: `<span style="background-color: #f97316; width: 12px; height: 12px; display: block; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></span>`,
              iconSize: [12, 12],
              iconAnchor: [6, 6]
            }),
          }).addTo(map).bindPopup("Inicio Conductor");

          L.marker(driverPoints[driverPoints.length - 1], {
            icon: L.divIcon({
              className: "leaflet-custom-dot",
              html: `<span style="background-color: #c2410c; width: 12px; height: 12px; display: block; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></span>`,
              iconSize: [12, 12],
              iconAnchor: [6, 6]
            }),
          }).addTo(map).bindPopup("Último Punto Conductor");
        }

        const allPoints = [...passengerPoints, ...driverPoints];
        if (allPoints.length > 0) {
          map.fitBounds(L.latLngBounds(allPoints), { padding: [20, 20] });
        }
      });
    }

    return () => {
      if (map) {
        map.remove();
      }
    };
  }, [isOpen, data]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="div-loading">
          <span className="txt-loading">Cargando logs de ruta...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="div-error">
          <span className="txt-error">{error}</span>
        </div>
      );
    }

    if (!data || !data.logs || data.logs.length === 0) {
      return (
        <div className="div-noData">
          <span className="txt-noData">No hay registro de ruta para este viaje.</span>
          <p style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
            Los registros de coordenadas comienzan a guardarse cada 30 segundos una vez iniciado el viaje.
          </p>
        </div>
      );
    }

    return (
      <div className="div-body">
        {/* Info Card */}
        <div className="div-tripCard">
          <div className="row">
            <span className="lbl-key">Pasajero</span>
            <span className="lbl-val">{passengerName || "—"}</span>
          </div>
          <div className="row">
            <span className="lbl-key">Conductor</span>
            <span className="lbl-val">{driverName || "—"}</span>
          </div>
          <div className="row">
            <span className="lbl-key">Puntos Registrados</span>
            <span className="lbl-val">{data.logs.length} puntos</span>
          </div>
          <div className="row" style={{ flexDirection: "column", alignItems: "flex-start", gap: "4px", marginTop: "12px", borderTop: "1px solid #f3f4f6", paddingTop: "12px" }}>
            <span className="lbl-key" style={{ marginBottom: "2px" }}>Origen</span>
            <span className="lbl-val" style={{ textAlign: "left", fontSize: "13px", color: "#1f2937", fontWeight: "bold" }}>
              {addresses?.origin?.title || "Cargando..."}
            </span>
            {addresses?.origin?.subtitle && (
              <span className="lbl-val" style={{ textAlign: "left", fontSize: "11px", color: "#4b5563" }}>
                {addresses.origin.subtitle}
              </span>
            )}
          </div>
          <div className="row" style={{ flexDirection: "column", alignItems: "flex-start", gap: "4px", marginTop: "12px", borderTop: "1px solid #f3f4f6", paddingTop: "12px" }}>
            <span className="lbl-key" style={{ marginBottom: "2px" }}>Destino</span>
            <span className="lbl-val" style={{ textAlign: "left", fontSize: "13px", color: "#1f2937", fontWeight: "bold" }}>
              {addresses?.destination?.title || "Cargando..."}
            </span>
            {addresses?.destination?.subtitle && (
              <span className="lbl-val" style={{ textAlign: "left", fontSize: "11px", color: "#4b5563" }}>
                {addresses.destination.subtitle}
              </span>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="div-legend">
          <div className="legend-item passenger">
            <div className="color-line" />
            <span>Pasajero</span>
          </div>
          <div className="legend-item driver">
            <div className="color-line" />
            <span>Conductor</span>
          </div>
        </div>

        {/* Leaflet Map Div */}
        <div>
          <span className="txt-sectionTitle">Mapa de Ruta Real</span>
          <div className="map-container-wrapper">
            <div id="leaflet-route-map" style={{ width: "100%", height: "400px", background: "#f0f0f0" }} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <Slide direction="left" in={isOpen} timeout={350} unmountOnExit>
      <Styles.DivMain>
        <div className="div-header">
          <label className="txt-title">Monitoreo de Ruta (Logs)</label>
          <VeryBtnTransparent
            CSS={[]}
            colorTheme={classVeryBtnTransparent.COLOR_THEME.BLACK_S2}
            icon={{
              name: "close",
              type: classVeryIconGoogle.ICON_TYPE.SYMBOLS_OUTLINED,
              size: 22,
            }}
            onClick={onClose}
          />
        </div>
        {renderContent()}
      </Styles.DivMain>
    </Slide>
  );
};
